import { useState, useEffect, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../App";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Search, Loader2, User, Mail, Phone, FileText, ClipboardList } from "lucide-react";

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/customers");
      setCustomers(response.data.customers);
    } catch {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await api.get(`/admin/customers/${customerId}`);
      setCustomerDetails(response.data);
    } catch (error) {
      toast.error("Failed to load customer details");
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetails(customer.customer_id);
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search) ||
      customer.phone?.toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700",
      estimated: "bg-yellow-100 text-yellow-700",
      survey_required: "bg-orange-100 text-orange-700",
      quoted: "bg-purple-100 text-purple-700",
      deposit_paid: "bg-green-100 text-green-700",
      confirmed: "bg-green-100 text-green-700",
      completed: "bg-slate-100 text-slate-700",
      cancelled: "bg-red-100 text-red-700",
      pending: "bg-yellow-100 text-yellow-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  return (
    <AdminLayout title="Customers">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Customers List */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="customers-search"
                />
              </div>
            </div>
            
            <div className="max-h-[600px] overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-[#FF5A1F]" />
                </div>
              )}
              {!loading && filteredCustomers.length === 0 && (
                <div className="p-8 text-center text-slate-500">No customers found</div>
              )}
              {!loading && filteredCustomers.length > 0 && (
                <div className="divide-y divide-slate-200">
                  {filteredCustomers.map((customer) => (
                    <button
                      key={customer.customer_id}
                      onClick={() => handleSelectCustomer(customer)}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                        selectedCustomer?.customer_id === customer.customer_id ? "bg-slate-50" : ""
                      }`}
                      data-testid={`customer-${customer.customer_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FF5A1F]/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-[#FF5A1F]" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-[#0F172A] truncate">{customer.name}</div>
                          <div className="text-sm text-slate-500 truncate">{customer.email}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="lg:col-span-2">
          {selectedCustomer && customerDetails ? (
            <div className="space-y-6">
              {/* Profile Card */}
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-[#FF5A1F]/10 flex items-center justify-center">
                    <User className="w-8 h-8 text-[#FF5A1F]" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-['Outfit'] font-bold text-[#0F172A]">
                      {selectedCustomer.name}
                    </h2>
                    <div className="mt-2 space-y-1 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${selectedCustomer.email}`} className="hover:text-[#FF5A1F]">
                          {selectedCustomer.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${selectedCustomer.phone}`} className="hover:text-[#FF5A1F]">
                          {selectedCustomer.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quotes */}
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#FF5A1F]" />
                  <h3 className="font-semibold text-[#0F172A]">Quotes ({customerDetails.quotes?.length || 0})</h3>
                </div>
                <div className="divide-y divide-slate-200">
                  {customerDetails.quotes?.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">No quotes</div>
                  ) : (
                    customerDetails.quotes?.map((quote) => (
                      <div key={quote.quote_id} className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{quote.from_postcode} → {quote.to_postcode}</span>
                          <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(quote.status)}`}>
                            {quote.status?.replace("_", " ")}
                          </span>
                        </div>
                        <div className="text-sm text-slate-500">
                          {quote.move_date} • {quote.bedrooms} bed {quote.property_type}
                        </div>
                        {quote.estimated_price && (
                          <div className="text-sm font-medium text-[#FF5A1F] mt-1">
                            £{quote.estimated_price.toFixed(2)}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Bookings */}
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#FF5A1F]" />
                  <h3 className="font-semibold text-[#0F172A]">Bookings ({customerDetails.bookings?.length || 0})</h3>
                </div>
                <div className="divide-y divide-slate-200">
                  {customerDetails.bookings?.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">No bookings</div>
                  ) : (
                    customerDetails.bookings?.map((booking) => (
                      <div key={booking.booking_id} className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono font-medium">{booking.booking_id?.slice(0, 8).toUpperCase()}</span>
                          <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {booking.status?.replace("_", " ")}
                          </span>
                        </div>
                        <div className="text-sm text-slate-500">
                          Deposit: £{booking.deposit_amount?.toFixed(2)} • {booking.deposit_paid ? "Paid" : "Pending"}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 p-8 text-center text-slate-500">
              <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Select a customer to view details</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCustomers;
